import styled from '@emotion/styled'

export interface StackProps {
  gap?: string
  align?: 'start' | 'center' | 'end' | 'stretch'
  justify?: 'start' | 'center' | 'end' | 'between' | 'around'
  inline?: boolean
}

const ALIGN: Record<NonNullable<StackProps['align']>, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
}

const JUSTIFY: Record<NonNullable<StackProps['justify']>, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
  around: 'space-around',
}

// Stack lays children out in a column with consistent gap. `inline`
// switches the box itself from block-level (`flex`) to inline-level
// (`inline-flex`) — useful when a Stack needs to sit next to text
// without breaking the line. To lay children horizontally, use `Row`.
export const Stack = styled.div<StackProps>`
  display: ${({ inline }) => (inline ? 'inline-flex' : 'flex')};
  flex-direction: column;
  gap: ${({ gap = 'var(--s-4)' }) => gap};
  align-items: ${({ align = 'stretch' }) => ALIGN[align]};
  justify-content: ${({ justify = 'start' }) => JUSTIFY[justify]};
`

// Row is Stack's horizontal sibling. Splitting these instead of adding a
// `direction` prop keeps the call sites readable: a Row is always a row.
// `inline` works the same way as on Stack — block-level vs inline-level
// flex container, with the row direction unchanged.
export const Row = styled.div<StackProps>`
  display: ${({ inline }) => (inline ? 'inline-flex' : 'flex')};
  flex-direction: row;
  gap: ${({ gap = 'var(--s-3)' }) => gap};
  align-items: ${({ align = 'center' }) => ALIGN[align]};
  justify-content: ${({ justify = 'start' }) => JUSTIFY[justify]};
`
